// src/contexts/BillingContext.tsx
import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import {
    loadInvoicesService,
    loadSubscriptionService,
    createCustomerPortalSessionService,
} from '@/services/billing/billingService';

const BillingContext = createContext<any | null>(null);

export const useBilling = () => {
    const context = useContext(BillingContext);
    if (!context) {
        throw new Error('useBilling must be used within a BillingProvider');
    }
    return context;
};

export const BillingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { organization, isAuthenticated } = useAuth();
    const [invoices, setInvoices] = useState<any[]>([]);
    const [subscription, setSubscription] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);

    const loadInvoices = async (shopId: string | number) => {
        const result = await loadInvoicesService(shopId);
        setInvoices(result.data || []);
    };

    const loadSubscription = async (shopId: string | number) => {
        const result = await loadSubscriptionService(shopId, organization?.email);
        setSubscription(result.data);
    };

    const loadData = async () => {
        if (!organization?.id) return;
        setLoading(true);

        try {
            await Promise.all([
                loadInvoices(organization.id),
                loadSubscription(organization.id),
            ]);
        } catch (error) {
            console.error('Errore nel caricamento dei dati di fatturazione:', error);
        } finally {
            setLoading(false);
        }
    };

    const createCustomerPortalSession = async () => {
        const result = await createCustomerPortalSessionService(subscription?.customer_id);

        if (result.success && result.url) {
            window.location.href = result.url;
        } else {
            throw new Error(result.error);
        }
    };

    useEffect(() => {
        if (isAuthenticated && organization?.id) {
            loadData();
        } else {
            setInvoices([]);
            setSubscription(null);
            setLoading(false);
        }
    }, [isAuthenticated, organization?.id]);

    const refreshData = () => {
        loadData();
    };

    const value = {
        invoices,
        subscription,
        loading,
        refreshData,
        createCustomerPortalSession,
    };

    return (
        <BillingContext.Provider value={value}>
            {children}
        </BillingContext.Provider>
    );
};